import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Construction,
  Delete,
  Info,
  LibraryBooks,
  Replay,
} from "@mui/icons-material";

import { ActivationSkeleton } from "./ActivationSkeleton";
import { buildActivation } from "@/api";
import {
  ActivationAudienceCard,
  ActivationForm,
  AddAudienceCard,
  HelpBox,
  useActivePage,
  ErrorPanel,
} from "@/components/atoms";
import {
  SelectableMenuCard,
} from "@/components/molecules";
import { PageContainer } from "@/components/containers";
import { useAudienceContext, useDialogs } from "@/context";
import { useActivation, useActivations, useAudiences } from "@/hooks";
import {
  activationLogos,
  activationStatusColorMap,
  activationStatusDescription,
  campaignTypeOptions,
  defaultActivation,
  getActivationBreadcrumb,
  isActivationExpired,
} from "@/utils";
import {
  ActivationAudience,
  ActivationStatus,
  Audience,
  Breadcrumb,
} from "@/types";

const ActivationPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { dispatch } = useAudienceContext();
  const navigate = useNavigate();

  let { activationId } = useParams<{ activationId: string }>();
  activationId = activationId === "new" ? undefined : activationId;
  const readOnly = !!activationId;
  const { audiences, loading: audiencesLoading } = useAudiences("build");
  const {
    activations,
    loading: activationsLoading,
    error: activationsError,
  } = useActivations();
  const {
    activation,
    loading: activationLoading,
    error: activationError,
  } = useActivation(activationId);
  const campaignType = activation?.campaign_type;
  const [availableAudiences, setAvailableAudiences] = useState<Audience[]>([]);
  const maxAudiences = campaignType === "meta" ? 1 : 5;

  const [buildLoading, setBuildLoading] = useState<boolean>(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  const activePage = useActivePage();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const { openDialog, closeDialog } = useDialogs();

  const formComplete =
    activation &&
    activation.audiences.length !== 0 &&
    activation.campaign_name !== "" &&
    activation.advertiser_name !== "" &&
    activation.agency_name !== "" &&
    activation.buyer_name !== "" &&
    activation.platform !== "" &&
    ((activation.platform === "DV360" &&
      activation.dv_campaign_type !== "" &&
      activation.dv_id_type !== "" &&
      activation.dv_currency !== "") ||
      activation.platform !== "DV360") &&
    (activation.seat_id !== "" || activation.campaign_type === "meta") &&
    activation.campaign_date_start !== "";

  useEffect(() => {
    activation &&
      audiences &&
      setAvailableAudiences(
        audiences.filter(
          (x) =>
            !activation.audiences
              .map((y) => y.audience_id)
              .includes(x.audience_id),
        ),
      );
  }, [activation, audiences]);

  useEffect(() => {
    setBreadcrumbs(getActivationBreadcrumb(activePage, activation));
  }, [activePage, activation]);

  const handleChangeType = (campaignType: string) => {
    if (campaignType === "meta") {
      activation &&
        dispatch({
          type: "UPDATE_ACTIVATION",
          payload: {
            ...defaultActivation,
            campaign_type: campaignType,
            platform: "Meta",
          },
        });
    } else {
      activation &&
        dispatch({
          type: "UPDATE_ACTIVATION",
          payload: { ...defaultActivation, campaign_type: campaignType },
        });
    }
  };

  const handleRemoveAudience = (audience: ActivationAudience) => {
    activation &&
      dispatch({
        type: "UPDATE_ACTIVATION",
        payload: {
          ...activation,
          audiences: activation.audiences.filter(
            (x) => x.audience_id !== audience.audience_id,
          ),
        },
      });
  };

  const handleAddAudiences = (audiences: Audience[]) => {
    if (!activation) return;

    const newAudiences = [
      ...activation.audiences,
      ...audiences.map((x) => ({
        audience_id: x.audience_id,
        audience_name: x.audience_name,
      })),
    ];

    dispatch({
      type: "UPDATE_ACTIVATION",
      payload: {
        ...activation,
        audiences: newAudiences,
      },
    });

    closeDialog();
  };

  const handleBuildActivation = async () => {
    setBuildError(null);
    setBuildLoading(true);

    if (!activation) return;
    const token = await getAccessTokenSilently();

    const { data, error } = await buildActivation(
      token,
      activation,
      activation.campaign_type,
    );

    if (data) {
      dispatch({
        type: "BUILD_ACTIVATION",
        payload: {
          activations: data.activations,
          currentActivation: data.activation,
        },
      });
      setBuildLoading(false);
      closeDialog();

      if (campaignType === "meta") {
        handleRecipeClick(data.activation.campaign_id);
      } else {
        navigate(`/activation`);
      }
    }

    if (error) {
      setBuildError(error.message);
      setBuildLoading(false);
    }
  };

  const handleRecipeClick = (campaignId?: string) => {
    campaignId && navigate(`/activation/${campaignId}/recipe`);
  };

  const handleOpenSelectDialog = () => {
    openDialog({
      type: 'selectAudience',
      props: {
        audiences: availableAudiences,
        onConfirm: handleAddAudiences,
      },
    });
  };

  const handleOpenConfirmDialog = () => {
    const message = campaignTypeOptions.find(x => x.value === campaignType)?.message || '';
    openDialog({
      type: 'confirm',
      props: {
        message: message,
        onConfirm: handleBuildActivation,
      },
    });
  };

  const handleOpenDeleteDialog = () => {
    openDialog({
      type: 'deleteActivation',
      props: {
        activation,
        redirect: '/activation',
      },
    });
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        color="error"
        disableElevation
        startIcon={<Delete />}
        disabled={buildLoading || !activationId}
        onClick={handleOpenDeleteDialog}
      >
        Delete
      </Button>
      {readOnly && campaignType === "eyeota" && (
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Replay />}
          disabled={!activationId || !formComplete}
          onClick={handleOpenConfirmDialog}
        >
          Resubmit
        </Button>
      )}
      {!readOnly && (
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Construction />}
          disabled={readOnly || !formComplete}
          onClick={handleOpenConfirmDialog}
        >
          Build
        </Button>
      )}
    </Stack>
  );

  return (
    <PageContainer
      breadcrumbs={activationError ? undefined : breadcrumbs}
      actions={activationError ? undefined : actionButtons}
      loading={buildLoading}
      loadingText={"Preparing Activation"}
    >
      {audiencesLoading || activationLoading || activationsLoading ? (
        <ActivationSkeleton />
      ) : (
        <Grid container spacing={2}>
          <Grid container size={12}>
            <ErrorPanel
              activationError={activationError ?? activationsError}
              buildError={buildError}
            />
          </Grid>
          {activation && audiences && (
            <>
              <Grid size={12} mb={1}>
                {/* Activation Name and Status */}
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">
                      {(activationId && activation.campaign_name) ??
                        "New Activation"}
                    </Typography>
                    {activationId && activation.status !== "" && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body1"
                          color={
                            activationStatusColorMap[
                              (isActivationExpired(activation)
                                ? "complete"
                                : activation.status) as ActivationStatus
                            ]
                          }
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          {isActivationExpired(activation)
                            ? "complete"
                            : activation.status}
                        </Typography>
                        <HelpBox
                          title={
                            activationStatusDescription[
                              (isActivationExpired(activation)
                                ? "complete"
                                : activation.status) as ActivationStatus
                            ]
                          }
                          placement="right"
                        >
                          <Info fontSize="small" color="info" />
                        </HelpBox>
                      </Stack>
                    )}
                  </Stack>
                  {readOnly &&
                    activationLogos[
                      campaignType as keyof typeof activationLogos
                    ]}
                </Box>
              </Grid>

              {readOnly && (
                <Grid size={12}>
                  <Alert variant="filled" severity="info">
                    Once an activation has been submitted it cannot be edited.
                    Please create a new activation if you wish to make changes.
                    We will soon be adding the ability to clone a previous
                    activation to make this process easier. In some
                    circumstances you may be asked by your account manager to
                    re-submit the audience, which you can do using the button in
                    the action bar above.
                  </Alert>
                </Grid>
              )}

              {/* Campaign Type Select */}
              {!readOnly && (
                <>
                  <Grid size={12}>
                    <Typography gutterBottom>Channel</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Define the channel you want to activate in
                    </Typography>
                  </Grid>
                  <Grid container size={12} spacing={2}>
                    {campaignTypeOptions.map((option, index) => (
                      <Grid key={index} pb={2}>
                        <SelectableMenuCard
                          title={option.label}
                          text={option.description}
                          onClick={() => handleChangeType(option.value)}
                          icon={option.icon}
                          disabled={option.disabled || readOnly}
                          selected={activation.campaign_type === option.value}
                          height="194px"
                          logo={
                            activationLogos[
                              option.value as keyof typeof activationLogos
                            ]
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {activation.campaign_type && (
                <>
                  {/* Audiences  */}
                  <Grid size={12}>
                    <Typography gutterBottom>Audience Segments</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Select the audiences you want to include in this
                      activation
                    </Typography>
                  </Grid>

                  <Grid container size={12} spacing={1}>
                    {activation.audiences.map((audience, idx) => (
                      <Grid key={idx}>
                        <ActivationAudienceCard
                          audience={audience}
                          onSelect={handleRemoveAudience}
                          disabled={readOnly}
                        />
                      </Grid>
                    ))}
                    {!readOnly &&
                      activation.audiences.length < maxAudiences && (
                        <Grid>
                          <AddAudienceCard
                            onClick={handleOpenSelectDialog}
                          />
                        </Grid>
                      )}
                  </Grid>
                  {activation.audiences.length === maxAudiences &&
                    !readOnly && (
                      <Typography variant="caption" color="textSecondary">
                        Maximum number of Segments reached
                      </Typography>
                    )}

                  {/* Empty Grid for spacing */}
                  <Grid size={12} />

                  {/* Activation Form  */}
                  <Grid size={12}>
                    <Typography gutterBottom>Campaign Details</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Fill in the information needed to create an activation.
                      Use the help boxes for more details
                    </Typography>
                  </Grid>
                  <ActivationForm
                    activation={activation}
                    activations={activations}
                    disabled={readOnly}
                  />

                  {/* Empty Grid for spacing */}
                  <Grid size={12} />

                  {/* Audience Recipe */}
                  {readOnly && activation.campaign_type === "meta" && (
                    <>
                      <Grid size={12}>
                        <Typography gutterBottom>Audience Recipe</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Use these interests to find your audience on Meta
                        </Typography>
                      </Grid>
                      <Grid size={12}>
                        <Button
                          variant="contained"
                          size="small"
                          disableElevation
                          startIcon={<LibraryBooks />}
                          onClick={() => handleRecipeClick(activationId)}
                        >
                          View Your Audience Recipe
                        </Button>
                        {/*{activation.meta_interests.map((interest, idx) => (*/}
                        {/*  <Typography>{interest.meta_name}</Typography>*/}
                        {/*))}*/}
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Dialogs  */}
              {/*<SelectAudienceDialog*/}
              {/*  open={selectDialogOpen}*/}
              {/*  audiences={availableAudiences}*/}
              {/*  onClose={handleCloseSelectDialog}*/}
              {/*  onConfirm={handleAddAudiences}*/}
              {/*  maxAudiences={maxAudiences}*/}
              {/*/>*/}
              {/*<ConfirmDialog*/}
              {/*  open={confirmDialogOpen}*/}
              {/*  message={*/}
              {/*    campaignType === "meta"*/}
              {/*      ? `Proceeding will mean that your activation will get submitted and an audience recipe will be created. */}
              {/*    You will have the chance to view and update this recipe on the next page.`*/}
              {/*      : `Proceeding will mean that your activation will get submitted to Eyeota and begin populating.*/}
              {/*    Please allow up to 7 days for it to be fully populated.`*/}
              {/*  }*/}
              {/*  onClose={handleCloseConfirmDialog}*/}
              {/*  onConfirm={handleBuildActivation}*/}
              {/*/>*/}
              {/*<DeleteActivationDialog*/}
              {/*  activation={activation}*/}
              {/*  open={deleteDialogOpen}*/}
              {/*  onClose={handleCloseDeleteDialog}*/}
              {/*  redirect="/activation"*/}
              {/*/>*/}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default ActivationPage;
